import type { Type } from '@angular/core';
import { InjectionToken } from '@angular/core';

// When adding/removing brands, also update the ingress hosts (<sparte>-<brand>.vergleich.${DOMAIN}) in k8s/ingress.yml
export const VALID_BRANDS = ['mlp'] as const;
export type Brand = (typeof VALID_BRANDS)[number];

export interface BrandFeatureFlags {
  showLogin: boolean;
  showFooter: boolean;
  showPoweredByCpit: boolean;
  showElektroVereinbarungen: boolean;
  emptyFieldsByDefault: boolean;
  useSelbstbehaltRecommendation: boolean;
  pinRecommendation: boolean;
  showAllTarifsUngrouped: boolean;
  showContactAlerts: boolean;
  showDuNote: boolean;
  /**
   * Renders the Makler name on step 12 as `"<firmenname>" für Kfz-Versicherungen GmbH`.
   * Brands whose `firmenname` is already the complete legal name set this to false and
   * get the bare name instead (KFZ-2458).
   */
  showMaklerKfzSuffix: boolean;
  /** MLP uses "Berater" in the set-order error hint; default brand uses "Vermittler/in". */
  setOrderHintUsesBerater: boolean;
  /** Preselect recommended coverages (Schutzbrief, Fahrerschutz, GAP, Vollkasko) and show "Empfohlen" badges (KFZ-2539). */
  useSchutzRecommendations: boolean;
  askNameOnSave: boolean;
  /**
   * On a rejected Vermittler (403), show "Rechner noch nicht freigeschaltet" with the hint to
   * open the comparit Vergleichsrechner. Brands without it get the generic error (KFZ-2725).
   */
  showVermittlerActivationHint: boolean;
  /** Replaces the intro of the c.AI Neuwert estimate with a disclaimer that the actual Neuwert may differ from it. */
  showNeuwertSchaetzungDisclaimer: boolean;
  /** Label for the Ergebnisliste forward button; brands without it get the bare arrow (KFZ-2786). */
  vergleichButtonLabel?: string;
}

export interface BrandEnvironment {
  apiUrl: string;
  portalUrl?: string;
}

export interface BrandConfig {
  id: Brand;
  displayName: string;
  features: BrandFeatureFlags;
  headerComponent?: Type<unknown>;
  headerSecondaryComponent?: Type<unknown>;
  loginBannerComponent?: Type<unknown>;
  footerComponent?: Type<unknown>;
  /**
   * The brand's own Erstinformation document. When set, step 12 "Informationen herunterladen"
   * downloads it instead of opening the generic Erstinformation dialog (KFZ-2718).
   */
  erstinformationPdfUrl?: string;
  /**
   * Session used for the Vermittler API when the app is opened without an
   * `?extVermittlerId=` query param. Not environment-specific (KFZ-2417).
   */
  fallbackSession?: string;
  /** Brand-specific floating customer-service contact box (KFZ-2482). */
  customerServiceBoxComponent?: Type<unknown>;
  /**
   * Brand-specific login dialog (KFZ-2397). Also opened on the restore route
   * before the session is hydrated (KFZ-2512), so it and anything it injects
   * must stay free of the app's form state.
   */
  loginDialogComponent?: Type<unknown>;
  /** Brand-specific message shown when "Anderer" is selected in the eVB Versicherer dropdown (KFZ-2537). */
  evbAndererMessageComponent?: Type<unknown>;
  /**
   * Brand-specific hint shown on step 10 once a user who is switching insurers ticks the
   * "SF-Klassen von Dritten übernehmen" checkbox, explaining that an SF-Rabatt cannot be taken
   * over from a third person through the calculator (KFZ-2548). Providing this component is also
   * what makes that checkbox appear, so the two ship together.
   */
  rabattUebertragungMessageComponent?: Type<unknown>;
  /** Brand-specific dialog shown on first restore of a Zeitsprung-imported task (KFZ-2541). */
  importHintDialogComponent?: Type<unknown>;
  /** Brand-specific backend API base URL. Overrides the default `environment.apiUrl` when set. */
  apiUrl?: string;
  /** External customer portal URL. When set and the user is logged in, logo clicks open this URL instead of navigating to step 1 (KFZ-2610). */
  portalUrl?: string;
  /** Brand-specific replacements for entries in the app's infotext map, keyed by infotext key (KFZ-2458). */
  infoTextOverrides?: Record<string, string>;
}

export const BRAND_REGISTRY = new InjectionToken<Partial<Record<Brand, BrandConfig>>>(
  'BRAND_REGISTRY',
  {
    factory: () => ({}),
  }
);
